import { identity, Observable, OperatorFunction } from 'rxjs';
import { defaultIfEmpty, filter, take, throwIfEmpty } from 'rxjs/operators';

export function firstWithContext<T, D = T>(
  predicate?: null,
  defaultValue?: D,
): OperatorFunction<T, T | D>;
export function firstWithContext<T, S extends T>(
  predicate: (value: T, index: number, source: Observable<T>) => value is S,
  defaultValue?: S,
): OperatorFunction<T, S>;
export function firstWithContext<T, S extends T, D>(
  predicate: (value: T, index: number, source: Observable<T>) => value is S,
  defaultValue: D,
): OperatorFunction<T, S | D>;
export function firstWithContext<T, D = T>(
  predicate: (value: T, index: number, source: Observable<T>) => boolean,
  defaultValue?: D,
): OperatorFunction<T, T | D>;
/**
 * Implementation of `first` that includes a stacktrace
 *
 * @param predicate An optional function called with each item to test for condition
 * matching.
 * @param defaultValue The default value emitted in case no valid value was found on
 * the source.
 * @return A function that returns an Observable that emits the first item that
 * matches the condition.
 */
export function firstWithContext<T, D>(
  predicate?: ((value: T, index: number, source: Observable<T>) => boolean) | null,
  defaultValue?: D,
): OperatorFunction<T, T | D> {
  let ifEmpty: OperatorFunction<T, T | D>;

  if (arguments.length >= 2) {
    ifEmpty = defaultIfEmpty<T, D>(defaultValue as D);
  } else {
    // Capture stacktrace here
    const emptyError = new Error('no elements in sequence');
    ifEmpty = throwIfEmpty(() => emptyError);
  }

  return (source: Observable<T>) =>
    source.pipe(predicate ? filter((v, i) => predicate(v, i, source)) : identity, take(1), ifEmpty);
}
